import { useLocation } from 'react-router-dom';

import { LitStachAuthModule } from '@/auth-conf';
import { getPathnameForContextParsing } from '@/utils/pathContext';

export type StackScope = 'system' | 'data-center' | 'console' | 'cluster';
/**
 * @description 确定当前组件位于哪个业务模块下。有些组件需要根据业务模块进行不同的渲染或逻辑处理。
 * - system - 系统模块
 * - data-center - 数据中心模块
 * - cluster - 集群模块 (从数据中心进入)
 * - console - 控制台模块
 * @returns 'system' | 'data-center' | 'console' | 'cluster' | undefined - 业务模块的名称。
 */
export function useRouteScope(): StackScope | undefined {
  const { pathname } = useLocation();
  return scopePath(pathname);
}

/** 纯函数 */
export const getRouteScope = (): StackScope | undefined => {
  const { pathname } = window.location;
  return scopePath(pathname);
};

/** 通用判断函数 */
function scopePath(pathname: string): StackScope | undefined {
  if (pathname.startsWith('/system/')) return 'system';
  if (pathname.startsWith('/data-center/')) return 'data-center';
  if (pathname.startsWith('/cluster/')) return 'cluster';
  if (pathname.startsWith('/console/')) return 'console';

  return undefined;
}

export function getModule(pathname?: string) {
  pathname = pathname || window.location.pathname;
  const arr = pathname.split('/');
  return arr?.[1];
}

/**
 * 门户页路由前缀：这些页面顶部展示动态产品菜单（与 /home、/product/all-skus 一致）。
 * 资源广场列表/详情统一挂在 /public 下。
 */
const PORTAL_NAV_PREFIXES = ['/home', '/product', '/about', '/public', '/intelligence-center', '/coupon-center'] as const;

/** 当前路由是否属于门户页（应展示顶部动态菜单） */
export function isPortalNavPath(pathname?: string): boolean {
  const path = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
  if (!path) return false;

  return PORTAL_NAV_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export function getPageModule(pathname?: string) {
  const arr = getPathnameForContextParsing(pathname).split('/');
  return arr?.[3];
}

/**
 * @description 将 StackScope 转换为 LitStachAuthModule
 * @param scope StackScope
 * @returns LitStachAuthModule
 */
export function convertScopeToAuthModule(scope: StackScope): LitStachAuthModule | undefined {
  const mapping: Record<StackScope, LitStachAuthModule> = {
    system: 'system',
    'data-center': 'data-center',
    cluster: 'cluster',
    console: 'tenant',
  };

  return mapping[scope];
}
